import { Archive as ArchiveIcon, ContentCopy, Edit, FilterAltOutlined as FilterAltOutlinedIcon } from '@mui/icons-material';
import { Box, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import useExecutionScope from '../../../hooks/useExecutionScope';
import { isExecutiveManager, showUserDisplayName } from '../../../lib/userUtils';
import { getConfigurations, getTestCaseData } from '../../../modules/config';
import {
  getFilterPresets
} from '../../../modules/datafilters';
import { getTestCases } from '../../../modules/jira';
import { getAssociatedUsers, getCurrentProject } from '../../../modules/project';
import TestCasesTable from '../../../ui/data/table/TableComponent';
import ActionButton from '../../../ui/primitives/ActionButton';
import SearchBar from '../../../ui/primitives/SearchBar';
import ExecutionScopeForm from '../../elements/common/execution/ExecutionScopeForm';
import { testCaseTableHead } from '../test-case/testCaseTableHead';
import AddTestCaseModal from './AddTestCaseModal';
import BulkEditModal from './bulk-edit/BulkEditModal';

import {
  buildTestCaseFilterConfig,
  TEST_CASE_FILTER_DEFAULTS,
} from '../../../features/testcases/Testcasefilterconfig';
import TestCaseTableActions from '../../../features/testcases/TestCaseTableActions';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  AppliedFilterChips,
  FilterModal,
} from '../../../ui/data/filters';
import { buildQueryString } from '../../../ui/data/filters/filterUtils';
import ActionMenu from '../../../ui/primitives/ActionMenu';

const DEFAULT_COLUMNS = [
  'key',
  'summary',
  'priorityName',
  'statusName',
  'testSuites',
  'module',
  'label',
  'owner',
  'updated_at',
];

const TestCasesTabContentComponent = () => {
  const [showModal, setShowModal] = useState(false);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [modules, setModules] = useState([]);
  const [labels, setLabels] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [technologyLayers, setTechnologyLayers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [isReviewWorkflow, setIsReviewWorkflow] = useState(false);
  const [orderBy, setOrderBy] = useState('created_at');
  const [projectUsers, setProjectUsers] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [savedFilters, setSavedFilters] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const [pendingAction, setPendingAction] = useState(null);

  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { isScopeDefined } = useExecutionScope(projectId);

  const [searchValue, setSearchValue] = useState(location.state?.searchValue || '');
  const [showArchived, setShowArchived] = useState(location.state?.showArchived || false);
  const debouncedSearchQuery = useDebounce(searchValue, 500);

  const aiHighlightKeysRef = useRef(
    location.state?.aiGeneratedKeys?.length > 0
      ? new Set(location.state.aiGeneratedKeys)
      : null
  );

  const STORAGE_KEY = `table-columns-testcases-${projectId}`;
  const reviewableStatusIds = useMemo(
    () =>
      statuses
        .filter((s) => s.name === 'In Review' || s.name === 'Approved')
        .map((s) => s.id),
    [statuses]
  );

  const filterConfig = useMemo(
    () =>
      buildTestCaseFilterConfig({
        isReviewWorkflow,
        reviewableStatuses: reviewableStatusIds,
      }),
    [isReviewWorkflow, reviewableStatusIds]
  );

  const {
    filters,
    setFilters,
    removeFilter,
    clearAllFilters,
    activeCount,
  } = useFilterState(filterConfig, location.state?.filters || TEST_CASE_FILTER_DEFAULTS);

  const projectUserOptions = useMemo(
    () => projectUsers.map((u) => ({ id: u.id, name: u.full_name })),
    [projectUsers]
  );

  const optionsMap = useMemo(
    () => ({
      status: statuses.filter((s) => s.name !== 'Archived'),
      priority: priorities,
      owner: projectUserOptions,
      reviewer: projectUserOptions,
      module: modules,
      technologyLayer: technologyLayers,
      labels,
      testSuites,
    }),
    [statuses, priorities, projectUserOptions, modules, technologyLayers, labels, testSuites]
  );

  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_COLUMNS;
    } catch {
      return DEFAULT_COLUMNS;
    }
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  const fetchTestCases = async (currentPage = 1, search = '', queryString = '') => {
    setLoading(true);
    try {
      const response = await getTestCases(
        showArchived, currentPage, perPage, search,
        projectId, queryString, orderBy, order
      );
      setTestCases(response?.testcases);
      setTotalCount(response?.total);
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to fetch test cases.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectData = async () => {
    const response = await getCurrentProject(projectId);
    setIsReviewWorkflow(response.review_workflow);
  };

  const fetchFilterPresets = async () => {
    if (!projectId) return;
    try {
      const response = await getFilterPresets(projectId, 'testcase');
      const normalized = (response?.data || []).map((preset) => {
        const {
          technology_layer,
          test_suites,
          cloud_device,
          is_automated,
          is_automation_needed,
          sub_platforms,
          ...rest
        } = preset.filters;

        return {
          ...preset,
          createdBy: preset.user_id,
          shareLevel: preset.share_level,
          filters: {
            ...rest,
            technologyLayer: technology_layer,
            testSuites: test_suites,
            cloudDevice: cloud_device,
            isAutomated: is_automated,
            isAutomationNeeded: is_automation_needed,
            subPlatforms: sub_platforms,
          },
        };
      });
      setSavedFilters(normalized);
    } catch (error) {
      console.error('Failed to fetch filter presets:', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchFilterPresets();
    }
  }, [projectId]);

  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const [usersResponse, response] = await Promise.all([
        getAssociatedUsers(projectId),
        getTestCaseData(projectId),
      ]);
      if (response) {
        setStatuses(response.result.filter((i) => i.group === 'TEST_CASE_STATUS'));
        setPriorities(response.result.filter((i) => i.group === 'TEST_CASE_PRIORITY'));
        setLabels(response.result.filter((i) => i.group === 'LABEL'));
        setModules(response.result.filter((i) => i.group === 'FUNCTIONAL_MODULE_FIELD'));
        setTechnologyLayers(response.result.filter((i) => i.group === 'TECHNOLOGY_LAYER'));
        setTestSuites(response.test_suites);
      }
      if (usersResponse) {
        setProjectUsers(usersResponse.map((u) => ({ ...u.user, role: u.role })));
      }
    } catch (error) {
      console.error('Error fetching test case data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const labelResponse = await getConfigurations('labels', projectId);
      setLabels(labelResponse?.data || []);
    } catch {
      console.log('Failed to fetch data.');
    }
  };

  useEffect(() => {
    const queryString = buildQueryString(filters);
    fetchTestCases(page, debouncedSearchQuery, queryString);
    navigate('.', { replace: true, state: { filters, searchValue, page, showArchived } });
  }, [showArchived, debouncedSearchQuery, filters, perPage, orderBy, order]);

  useEffect(() => {
    fetchTestCases(page, searchValue, buildQueryString(filters));
  }, [page]);

  useEffect(() => {
    fetchTestCaseData();
    fetchProjectData();
  }, []);

  useEffect(() => {
    if (!aiHighlightKeysRef.current || testCases.length === 0) return;
    const highlightKeys = aiHighlightKeysRef.current;
    const matchingIds = testCases
      .filter((tc) => highlightKeys.has(tc.key))
      .map((tc) => tc.id);
    if (matchingIds.length > 0) {
      setSelected(matchingIds);
      aiHighlightKeysRef.current = null;
    }
  }, [testCases]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    navigate('.', { replace: true, state: { filters, searchValue, page: newPage, showArchived } });
  };

  const handlePageSizeChange = (e) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(1);
    navigate('.', { replace: true, state: { filters, searchValue, page: 1, showArchived } });
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleShowArchived = () => {
    setShowArchived((prev) => !prev);
    handlePageChange(1);
    setSelected([]);
  };

  const handleClearAll = () => {
    clearAllFilters();
    setSearchValue('');
    setPage(1);
    navigate('.', { replace: true, state: { filters: TEST_CASE_FILTER_DEFAULTS, searchValue: '', page: 1 } });
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const refreshData = () => {
    setSelected([]);
    fetchTestCases(page, searchValue, buildQueryString(filters));
  };

  const formatedTestCases = useMemo(() => {
    return testCases.map((tc) => ({
      ...tc,
      owner: showUserDisplayName(tc.owner, projectUsers),
      reviewer: showUserDisplayName(tc.reviewer, projectUsers),
      approved_by: showUserDisplayName(tc.approved_by, projectUsers),
      label:
        tc.labels?.length > 0
          ? tc.labels.map((id) => labels.find((l) => l.id === id)?.name).join(', ')
          : '-',
      module: tc.module?.name || '-',
      statusName: tc.status?.name || '-',
      priorityName: tc.priority?.name || '-',
      technologyLayer: tc.technology_layer || '-',
      isAutomated: tc.is_automated || '-',
      candidateForAutomation: tc.candidate_for_automation ?? '-',
      testSuites:
        tc.test_suites?.length > 0
          ? tc.test_suites.map((id) => testSuites.find((s) => s.id === id)?.name || '-').join(', ')
          : '-',
    }));
  }, [testCases, projectUsers, labels, testSuites]);

  const executionScopeElement = <ExecutionScopeForm isPlatformRequired={false} />;

  const handleEdit = (row) => {
    navigate(`/project/${projectId}/testcases/${row.key}`, { state: { testCaseId: row.id, isEdit: true } });
  };

  const handleClone = (row) => setPendingAction({ type: 'clone', row });

  const handleArchive = (row) => setPendingAction({ type: 'archive', row });

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Test Cases
      </Typography>
      <Grid container alignItems="center" justifyContent="space-between" wrap="wrap" gap={1}>
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Test Cases"
          />
          <ActionButton
            onClick={() => setShowFilterModal(true)}
            label="Filters"
            icon={<FilterAltOutlinedIcon />}
            badgeContent={activeCount}
          />
        </Grid>
        {!isExecutiveManager() && (
          <TestCaseTableActions
            projectId={projectId}
            handleOpenTestCaseModal={() => setShowModal(true)}
            isSelectedItems={selected.length}
            seletectItems={selected}
            showArchived={showArchived}
            refreshData={refreshData}
            selectedRows={selectedRows}
            setShowArchived={handleShowArchived}
            handleBulkEditModal={(v) => setIsBulkEditOpen(v)}
            setSelected={setSelected}
            fetchTestCaseData={fetchTestCaseData}
            pendingAction={pendingAction}
            setPendingAction={setPendingAction}
          />
        )}
      </Grid>
      {(activeCount > 0 || searchValue) && (
        <Box sx={{ mt: 2 }}>
          <AppliedFilterChips
            filters={filters}
            filterConfig={filterConfig}
            optionsMap={optionsMap}
            onRemove={removeFilter}
            onClearAll={handleClearAll}
          />
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <TestCasesTable
          data={formatedTestCases || []}
          headerTabs={testCaseTableHead}
          showCheckbox={!isExecutiveManager()}
          page={page}
          perPage={perPage}
          setPerPage={handlePageSizeChange}
          order={order}
          setOrder={setOrder}
          defaultSort="created_at"
          selected={selected}
          isLoading={loading}
          striped
          setSelectedRows={setSelectedRows}
          setSelected={setSelected}
          totalCount={totalCount}
          showPages
          handlePageChange={handlePageChange}
          tablekey="test cases"
          showCount
          showRowCount
          showColumnManager
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          mandatoryKeys={['key', 'summary']}
          defaultColumns={DEFAULT_COLUMNS}
          serverSideSorting
          onRefresh={refreshData}
          onSortChange={(key, direction) => {
            setOrderBy(key);
            setOrder(direction);
            setPage(1);
          }}
          action={(row) => (
            !showArchived && !isExecutiveManager() && (
              <ActionMenu
                items={[
                  {
                    label: 'Edit',
                    icon: <Edit />,
                    onClick: () => handleEdit(row),
                  },
                  {
                    label: 'Clone',
                    icon: <ContentCopy />,
                    onClick: () => handleClone(row),
                  },
                  {
                    label: 'Archive',
                    icon: <ArchiveIcon />,
                    color: 'error',
                    onClick: () => handleArchive(row),
                  },
                ]}
              />
            )
          )}
        />
      </Box>
      <FilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={handleApplyFilters}
        filterConfig={filterConfig}
        currentFilters={filters}
        optionsMap={optionsMap}
        title="Filter Test Cases"
        showExecutionScope={isScopeDefined}
        ExecutionScopeComponent={isScopeDefined ? executionScopeElement : null}
        savedFilters={savedFilters}
        refreshPresets={fetchFilterPresets}
        userRole={currentUser?.role}
        projectId={projectId}
        filterType="testcase"
        currentUserId={currentUser?.id}
      />
      <AddTestCaseModal
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        fetchTestCases={fetchTestCases}
        projectUsers={projectUsers || []}
        statuses={statuses}
        priorities={priorities}
        testSuites={testSuites}
        projectId={projectId}
        labels={labels}
        modules={modules}
        loading={loading}
        isReviewWorkflow={isReviewWorkflow}
        refetchData={() => { fetchTestCaseData(); refreshData(); }}
        technologyLayers={technologyLayers}
        postFeedbackAction="refetch"
      />
      <BulkEditModal
        isOpen={isBulkEditOpen}
        onClose={(v) => setIsBulkEditOpen(v)}
        selectedTestCases={selectedRows}
        priorities={priorities}
        statuses={statuses}
        labels={labels}
        modules={modules}
        testSuites={testSuites}
        loading={loading}
        isReviewWorkflow={isReviewWorkflow}
        projectUsers={projectUsers}
        technologyLayers={technologyLayers}
        fetchTestCases={refreshData}
        fetchDropdownData={fetchDropdownData}
      />
    </Container>
  );
};

export default TestCasesTabContentComponent;
